import { oc } from "@orpc/contract";
import { z } from "zod";
import { CustomerEmail, PersonName, TurnstileToken } from "./customer-account";

/**
 * The shop's contact form (frontend spec, "Content, contact en nieuwsbrief"; CONTEXT.md
 * `contactMessage`): mailed to the customer-service address from Bedrijfsgegevens with Reply-To
 * the customer, and a short receipt to the customer. Nothing is stored but the mail log.
 *
 * A guest passes Turnstile (`turnstileToken`); a signed-in customer (the customer session cookie)
 * does not need to.
 */
export const ContactMessageInput = z.object({
	name: PersonName,
	email: CustomerEmail,
	/** The order it is about, as the customer types it (`CBO-100123`); empty: none. */
	orderNumber: z
		.string()
		.trim()
		.max(30)
		.optional()
		.transform((value) => (value === "" ? undefined : value)),
	subject: z.string().trim().min(1).max(150),
	message: z.string().trim().min(1).max(5000),
	/** Required for a guest. Empty counts as none: the form sends the field even without a widget. */
	turnstileToken: z
		.union([z.literal(""), TurnstileToken])
		.optional()
		.transform((value) => (value === "" ? undefined : value)),
});
export type ContactMessageInput = z.input<typeof ContactMessageInput>;
export type ContactMessage = z.output<typeof ContactMessageInput>;

/**
 * Public. Answers 204 once the message reached customer service. Errors: `ContactMessageInvalid`
 * (400) with `issues`, each `{ field, code }` (Zod's issue code); `ContactBotCheckFailed` (403) and `ContactBotCheckUnavailable` (503) for a guest's
 * Turnstile; `RateLimited` (429): too many messages from this IP or for this email address;
 * `ContactUnavailable` (503): customer service has no address yet, or the mail did not go out.
 */
export const ContactMessageContract = {
	send: oc.route({ method: "POST", path: "/shop/contact", successStatus: 204 }).input(ContactMessageInput),
};
